import {
  Component,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  Injector,
  Input,
  Signal,
} from '@angular/core';
import {
  CmsComponentData,
  GlobalMessageComponentCmsComponentData,
  GlobalMessageEntities,
  GlobalMessageType,
} from '@boels/models';
import { GlobalMessageFacade } from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, tap } from 'rxjs';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';
import { SafeHtmlPipe } from '@boels/helpers';

@Component({
  selector: 'boels-global-message',
  templateUrl: './global-message.component.html',
  styleUrls: ['./global-message.component.scss'],
  standalone: true,
  imports: [
    FontAwesomeModule,
    CommonModule,
    VerticalAlignerComponent,
    SafeHtmlPipe,
  ],
})
export class GlobalMessageComponent {
  @Input()
  componentData: CmsComponentData<GlobalMessageComponentCmsComponentData> | null =
    null;
  #globalMessageService: GlobalMessageFacade = inject(GlobalMessageFacade);
  #router: Router = inject(Router);
  #injector: Injector = inject(Injector);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #host: ElementRef = inject(ElementRef);

  faClose = faTimes;
  faSuccess = faCheckCircle;
  faInfo = faInfoCircle;
  faWarning = faExclamationTriangle;
  faError = faExclamationCircle;

  $messages: Signal<GlobalMessageEntities | undefined> = toSignal(
    this.#globalMessageService.get(),
  );
  closeMsgOnNav: EffectRef = effect(
    () => {
      const messages = this.$messages();
      this.#router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd,
          ),
          tap(() => {
            if (!this.#globalMessageService.preserveOnRedirect) {
              if (messages && Object.keys(messages).length > 0) {
                Object.keys(messages).forEach((key, index) => {
                  this.clear(key, index);
                });
              }
            } else {
              this.#globalMessageService.preserveOnRedirect = false;
            }
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    },
    { injector: this.#injector },
  );

  clear(type: string, index: number): void {
    // If index is negative, it means the message is provided with cms component data
    if (index >= 0) {
      this.#globalMessageService.remove(type as GlobalMessageType, index);
    } else {
      this.#host.nativeElement.remove();
    }
  }

  getMessageTypes(
    entities: GlobalMessageEntities | undefined,
  ): string[] | undefined {
    return entities && Object.keys(entities);
  }

  getMessageClass(type: string): string {
    switch (type) {
      case GlobalMessageType.MSG_TYPE_CONFIRMATION:
        return '-success';
      case GlobalMessageType.MSG_TYPE_INFO:
        return '-info';
      case GlobalMessageType.MSG_TYPE_WARNING:
        return '-warning';
      case GlobalMessageType.MSG_TYPE_ERROR:
        return '-danger';
      default:
        throw new Error('Unknown GlobalMessageType');
    }
  }

  getMessageIcon(type: string): IconDefinition {
    switch (type) {
      case GlobalMessageType.MSG_TYPE_CONFIRMATION:
        return this.faSuccess;
      case GlobalMessageType.MSG_TYPE_INFO:
        return this.faInfo;
      case GlobalMessageType.MSG_TYPE_WARNING:
        return this.faWarning;
      case GlobalMessageType.MSG_TYPE_ERROR:
        return this.faError;
      default:
        throw new Error('Unknown GlobalMessageType');
    }
  }

  protected readonly GlobalMessageType = GlobalMessageType;
}
